import { useState, useRef, useEffect, useCallback } from 'react';
import { ArrowLeft, X, Send, Trash2 } from 'lucide-react';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { streamChat } from '../../lib/ai';
import { ChatMessage } from './ChatMessage';
import { SuggestedQuestions } from './SuggestedQuestions';

export interface ChatPanelContentProps {
  onClose: () => void;
  showBack?: boolean;
}

export function ChatPanelContent({ onClose, showBack = false }: ChatPanelContentProps) {
  const messages = useSimulatorStore(s => s.chatMessages);
  const addMessage = useSimulatorStore(s => s.addChatMessage);
  const updateLast = useSimulatorStore(s => s.updateLastAssistantMessage);
  const clearChat = useSimulatorStore(s => s.clearChat);

  const assumptions = useSimulatorStore(s => s.assumptions);
  const packages = useSimulatorStore(s => s.packages);
  const activeTab = useSimulatorStore(s => s.activeTab);
  const selectedPrice = useSimulatorStore(s => s.selectedPrice);

  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || streaming) return;

    const userMsg = { role: 'user' as const, content: text.trim() };
    addMessage(userMsg);
    addMessage({ role: 'assistant', content: '' });
    setInput('');
    setStreaming(true);

    let accumulated = '';
    const allMessages = [...messages, userMsg].map(m => ({ role: m.role, content: m.content }));

    await streamChat(
      allMessages,
      { assumptions, packages, activeTab, selectedPrice },
      (chunk) => {
        accumulated += chunk;
        updateLast(accumulated);
      },
      () => setStreaming(false),
      (err) => {
        updateLast(accumulated + `\n\n*Error: ${err}*`);
        setStreaming(false);
      },
    );
  }, [streaming, messages, assumptions, packages, activeTab, selectedPrice, addMessage, updateLast]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <div className="flex flex-col flex-1 min-h-0" style={{ backgroundColor: 'var(--color-card)' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 md:px-5 py-2.5 md:py-3.5 border-b shrink-0" style={{ borderColor: 'var(--color-border)' }}>
        <div className="flex items-center gap-2">
          {showBack && (
            <button onClick={onClose} className="p-1.5 text-muted hover:text-primary transition-colors rounded -ml-1" aria-label="Back">
              <ArrowLeft size={18} />
            </button>
          )}
          <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: 'linear-gradient(135deg, rgba(13,148,136,0.12) 0%, rgba(37,99,235,0.08) 100%)' }}
          >
            <span className="text-chart-teal text-xs font-bold">AI</span>
          </div>
          <div className="min-w-0">
            <h2 className="text-sm font-bold text-primary truncate">AI Analyst</h2>
            <p className="text-micro text-muted tracking-wide">Powered by GHOST</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <button onClick={clearChat} className="p-1.5 text-muted hover:text-secondary transition-colors rounded" title="Clear chat">
              <Trash2 size={14} />
            </button>
          )}
          {!showBack && (
            <button onClick={onClose} className="p-1.5 text-muted hover:text-primary transition-colors rounded" aria-label="Close">
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      {messages.length === 0 ? (
        <SuggestedQuestions onSelect={sendMessage} />
      ) : (
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 min-h-0">
          {messages.map((msg, i) => (
            <ChatMessage
              key={`${msg.role}-${i}-${msg.content.slice(0, 20)}`}
              role={msg.role}
              content={msg.content}
              isStreaming={streaming && i === messages.length - 1 && msg.role === 'assistant'}
            />
          ))}
          <div ref={messagesEndRef} />
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSubmit} className="px-4 py-3 border-t shrink-0" style={{ borderColor: 'var(--color-border)', paddingBottom: showBack ? 'calc(60px + env(safe-area-inset-bottom, 0px))' : 'max(0.75rem, env(safe-area-inset-bottom))' }}>
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder={streaming ? 'Thinking...' : 'Ask about the data...'}
            disabled={streaming}
            className="flex-1 input-field text-base md:text-sm py-2"
          />
          <button
            type="submit"
            disabled={!input.trim() || streaming}
            className="p-2.5 rounded-xl bg-chart-teal text-white transition-all hover:bg-teal-600 hover:shadow-glow-teal disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
          >
            <Send size={16} />
          </button>
        </div>
      </form>
    </div>
  );
}
